import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import {
    TextB,
    TextItalic,
    TextUnderline,
    TextAlignLeft,
    TextAlignCenter,
    TextAlignRight,
    TextAlignJustify,
    ListBullets,
    ListNumbers,
    Eraser,
    Palette,
    Highlighter,
    Link as LinkIcon,
    LinkBreak,
    X
} from '@phosphor-icons/react';

const PRESET_COLORS = [
    '#1F2D4B', // primary
    '#FB7185', // accent
    '#000000',
    '#4B5878',
    '#8B8474',
    '#2D7A52', // green
    '#C8413A', // red
    '#1E40AF', // blue
    '#7C3AED', // purple
    '#D97706', // amber
];

const HIGHLIGHT_COLORS = [
    '#FEF08A', // yellow (typical highlighter)
    '#FED7AA', // peach
    '#FECACA', // pink
    '#BBF7D0', // mint
    '#BFDBFE', // sky
    '#DDD6FE', // lavender
    '#FDE68A', // amber
    '#A7F3D0', // teal
    '#F9A8D4', // rose
    '#E5E7EB', // gray
];

/**
 * WYSIWYG rich text editor based on TipTap.
 * Stores content as HTML string.
 * Supports: bold, italic, underline, lists, text color.
 * Touch-friendly (36-40px tap targets) for iPad and Android tablets.
 *
 * Props:
 *  - value: HTML string
 *  - onChange: (html) => void
 *  - placeholder: string
 *  - testId: string
 *  - minHeight: css string (e.g. '120px')
 *  - helperText: string
 */
const RichTextEditor = ({
    value,
    onChange,
    placeholder,
    testId,
    minHeight = '120px',
    helperText
}) => {
    const [showColorPicker, setShowColorPicker] = React.useState(false);
    const [showHighlightPicker, setShowHighlightPicker] = React.useState(false);
    const [showLinkInput, setShowLinkInput] = React.useState(false);
    const [linkUrl, setLinkUrl] = React.useState('');
    const pickerRef = useRef(null);
    const highlightRef = useRef(null);
    const linkRef = useRef(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                horizontalRule: false,
                codeBlock: false,
                code: false,
            }),
            TextStyle,
            Color,
            Underline,
            Highlight.configure({ multicolor: true }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    rel: 'noopener noreferrer',
                    target: '_blank',
                    class: 'cert-link',
                },
            }),
            TextAlign.configure({
                types: ['paragraph'],
                alignments: ['left', 'center', 'right', 'justify'],
                defaultAlignment: 'left',
            }),
            Placeholder.configure({ placeholder: placeholder || 'Escribe aquí...' }),
        ],
        content: value || '',
        editorProps: {
            attributes: {
                class: 'tiptap-content prose prose-sm max-w-none focus:outline-none',
                style: `min-height: ${minHeight};`,
                'data-testid': testId ? `${testId}-area` : undefined,
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            // Treat empty paragraph as truly empty so saved field is clean
            onChange(html === '<p></p>' ? '' : html);
        },
    });

    // Sync external value changes (e.g. when reopening modal with new product)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const incoming = value || '';
        // Only update if different to avoid losing focus while typing
        if (incoming !== current && incoming !== (current === '<p></p>' ? '' : current)) {
            editor.commands.setContent(incoming || '', false);
        }
    }, [value, editor]);

    // Close popovers on outside click
    useEffect(() => {
        if (!showColorPicker && !showHighlightPicker && !showLinkInput) return;
        const onClick = (e) => {
            if (showColorPicker && pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowColorPicker(false);
            }
            if (showHighlightPicker && highlightRef.current && !highlightRef.current.contains(e.target)) {
                setShowHighlightPicker(false);
            }
            if (showLinkInput && linkRef.current && !linkRef.current.contains(e.target)) {
                setShowLinkInput(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [showColorPicker, showHighlightPicker, showLinkInput]);

    if (!editor) return null;

    const btn = (active, onClick, children, ariaLabel, dataTestId) => (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            aria-label={ariaLabel}
            data-testid={dataTestId}
            className={`h-10 min-w-[40px] px-2.5 flex items-center justify-center rounded-md border text-sm font-medium transition-colors touch-manipulation ${
                active
                    ? 'bg-[#1F2D4B] text-white border-[#1F2D4B]'
                    : 'bg-white text-[#1F2D4B] border-[#ECE3D2] hover:bg-[#F8F4EC]'
            }`}
        >
            {children}
        </button>
    );

    const applyColor = (color) => {
        editor.chain().focus().setColor(color).run();
        setShowColorPicker(false);
    };

    const clearColor = () => {
        editor.chain().focus().unsetColor().run();
        setShowColorPicker(false);
    };

    const applyHighlight = (color) => {
        editor.chain().focus().setHighlight({ color }).run();
        setShowHighlightPicker(false);
    };

    const clearHighlight = () => {
        editor.chain().focus().unsetHighlight().run();
        setShowHighlightPicker(false);
    };

    const openLinkInput = () => {
        // Pre-fill with existing link href if cursor is on a link
        const existing = editor.getAttributes('link').href || '';
        setLinkUrl(existing);
        setShowLinkInput(true);
    };

    const applyLink = () => {
        if (!linkUrl.trim()) {
            editor.chain().focus().unsetLink().run();
        } else {
            let url = linkUrl.trim();
            // Auto-prepend https:// if missing protocol
            if (!/^(https?:|mailto:|tel:)/.test(url)) {
                url = 'https://' + url;
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
        setShowLinkInput(false);
        setLinkUrl('');
    };

    const removeLink = () => {
        editor.chain().focus().unsetLink().run();
        setShowLinkInput(false);
        setLinkUrl('');
    };

    const clearAll = () => {
        editor.chain().focus().clearNodes().unsetAllMarks().run();
    };

    return (
        <div className="w-full" data-testid={testId}>
            <div className="flex items-center gap-1 mb-1.5 flex-wrap relative">
                {btn(editor.isActive('bold'),
                    () => editor.chain().focus().toggleBold().run(),
                    <TextB size={16} weight="bold" />,
                    'Negrita',
                    testId ? `${testId}-bold` : undefined)}

                {btn(editor.isActive('italic'),
                    () => editor.chain().focus().toggleItalic().run(),
                    <TextItalic size={16} weight="bold" />,
                    'Cursiva',
                    testId ? `${testId}-italic` : undefined)}

                {btn(editor.isActive('underline'),
                    () => editor.chain().focus().toggleUnderline().run(),
                    <TextUnderline size={16} weight="bold" />,
                    'Subrayado',
                    testId ? `${testId}-underline` : undefined)}

                <span className="w-px h-6 bg-[#ECE3D2] mx-0.5" />

                {/* Alignment — Left is also "active" when no alignment is set (default) */}
                {btn(editor.isActive({ textAlign: 'left' }) || (!editor.isActive({ textAlign: 'center' }) && !editor.isActive({ textAlign: 'right' }) && !editor.isActive({ textAlign: 'justify' })),
                    () => editor.chain().focus().setTextAlign('left').run(),
                    <TextAlignLeft size={16} weight="bold" />,
                    'Alinear a la izquierda',
                    testId ? `${testId}-align-left` : undefined)}
                {btn(editor.isActive({ textAlign: 'center' }),
                    () => editor.chain().focus().setTextAlign('center').run(),
                    <TextAlignCenter size={16} weight="bold" />,
                    'Centrar',
                    testId ? `${testId}-align-center` : undefined)}
                {btn(editor.isActive({ textAlign: 'right' }),
                    () => editor.chain().focus().setTextAlign('right').run(),
                    <TextAlignRight size={16} weight="bold" />,
                    'Alinear a la derecha',
                    testId ? `${testId}-align-right` : undefined)}
                {btn(editor.isActive({ textAlign: 'justify' }),
                    () => editor.chain().focus().setTextAlign('justify').run(),
                    <TextAlignJustify size={16} weight="bold" />,
                    'Justificar',
                    testId ? `${testId}-align-justify` : undefined)}

                <span className="w-px h-6 bg-[#ECE3D2] mx-0.5" />

                {btn(editor.isActive('bulletList'),
                    () => editor.chain().focus().toggleBulletList().run(),
                    <ListBullets size={16} weight="bold" />,
                    'Lista con viñetas',
                    testId ? `${testId}-bullets` : undefined)}

                {btn(editor.isActive('orderedList'),
                    () => editor.chain().focus().toggleOrderedList().run(),
                    <ListNumbers size={16} weight="bold" />,
                    'Lista numerada',
                    testId ? `${testId}-numbers` : undefined)}

                <span className="w-px h-6 bg-[#ECE3D2] mx-0.5" />

                {/* Color picker */}
                <div className="relative" ref={pickerRef}>
                    {btn(showColorPicker,
                        () => setShowColorPicker(v => !v),
                        <>
                            <Palette size={16} weight="bold" />
                            <span
                                className="ml-1 w-3 h-3 rounded-full border border-[#ECE3D2]"
                                style={{ backgroundColor: editor.getAttributes('textStyle').color || '#1F2D4B' }}
                            />
                        </>,
                        'Color de texto',
                        testId ? `${testId}-color-btn` : undefined)}

                    {showColorPicker && (
                        <div
                            className="absolute z-50 top-full left-0 mt-1 bg-white border border-[#ECE3D2] rounded-lg shadow-lg p-3 w-[240px]"
                            data-testid={testId ? `${testId}-color-panel` : undefined}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] uppercase tracking-wider text-[#8B8474] font-semibold">Color de texto</span>
                                <button type="button" onClick={() => setShowColorPicker(false)} className="text-[#8B8474] hover:text-[#1F2D4B]">
                                    <X size={14} />
                                </button>
                            </div>
                            <div className="grid grid-cols-5 gap-1.5">
                                {PRESET_COLORS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => applyColor(c)}
                                        className="h-8 w-8 rounded-md border border-[#ECE3D2] hover:scale-110 active:scale-95 transition-transform"
                                        style={{ backgroundColor: c }}
                                        title={c}
                                        data-testid={testId ? `${testId}-color-${c.replace('#','')}` : undefined}
                                    />
                                ))}
                            </div>
                            <div className="mt-3 pt-3 border-t border-[#ECE3D2] flex items-center gap-2">
                                <label className="text-[11px] text-[#4B5878] flex-1">Personalizado:</label>
                                <input
                                    type="color"
                                    onChange={(e) => applyColor(e.target.value)}
                                    className="w-9 h-8 rounded border border-[#ECE3D2] cursor-pointer"
                                    data-testid={testId ? `${testId}-color-custom` : undefined}
                                />
                                <button
                                    type="button"
                                    onClick={clearColor}
                                    className="text-[11px] text-[#FB7185] hover:underline px-2"
                                >
                                    Quitar
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <span className="w-px h-6 bg-[#ECE3D2] mx-0.5" />

                {/* Highlight picker */}
                <div className="relative" ref={highlightRef}>
                    {btn(editor.isActive('highlight') || showHighlightPicker,
                        () => {
                            setShowHighlightPicker(v => !v);
                            setShowColorPicker(false);
                            setShowLinkInput(false);
                        },
                        <>
                            <Highlighter size={16} weight="bold" />
                            <span
                                className="ml-1 w-3 h-3 rounded-full border border-[#ECE3D2]"
                                style={{ backgroundColor: editor.getAttributes('highlight').color || '#FEF08A' }}
                            />
                        </>,
                        'Resaltar texto',
                        testId ? `${testId}-highlight-btn` : undefined)}

                    {showHighlightPicker && (
                        <div
                            className="absolute z-50 top-full left-0 mt-1 bg-white border border-[#ECE3D2] rounded-lg shadow-lg p-3 w-[240px]"
                            data-testid={testId ? `${testId}-highlight-panel` : undefined}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] uppercase tracking-wider text-[#8B8474] font-semibold">Resaltado</span>
                                <button type="button" onClick={() => setShowHighlightPicker(false)} className="text-[#8B8474] hover:text-[#1F2D4B]">
                                    <X size={14} />
                                </button>
                            </div>
                            <div className="grid grid-cols-5 gap-1.5">
                                {HIGHLIGHT_COLORS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => applyHighlight(c)}
                                        className="h-8 w-8 rounded-md border border-[#ECE3D2] hover:scale-110 active:scale-95 transition-transform"
                                        style={{ backgroundColor: c }}
                                        title={c}
                                        data-testid={testId ? `${testId}-highlight-${c.replace('#','')}` : undefined}
                                    />
                                ))}
                            </div>
                            <div className="mt-3 pt-3 border-t border-[#ECE3D2] flex items-center gap-2">
                                <label className="text-[11px] text-[#4B5878] flex-1">Personalizado:</label>
                                <input
                                    type="color"
                                    onChange={(e) => applyHighlight(e.target.value)}
                                    className="w-9 h-8 rounded border border-[#ECE3D2] cursor-pointer"
                                />
                                <button
                                    type="button"
                                    onClick={clearHighlight}
                                    className="text-[11px] text-[#FB7185] hover:underline px-2"
                                >
                                    Quitar
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Link input */}
                <div className="relative" ref={linkRef}>
                    {btn(editor.isActive('link') || showLinkInput,
                        () => {
                            if (showLinkInput) {
                                setShowLinkInput(false);
                            } else {
                                openLinkInput();
                                setShowColorPicker(false);
                                setShowHighlightPicker(false);
                            }
                        },
                        <LinkIcon size={16} weight="bold" />,
                        'Insertar enlace',
                        testId ? `${testId}-link-btn` : undefined)}

                    {showLinkInput && (
                        <div
                            className="absolute z-50 top-full left-0 mt-1 bg-white border border-[#ECE3D2] rounded-lg shadow-lg p-3 w-[300px]"
                            data-testid={testId ? `${testId}-link-panel` : undefined}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] uppercase tracking-wider text-[#8B8474] font-semibold">Enlace</span>
                                <button type="button" onClick={() => setShowLinkInput(false)} className="text-[#8B8474] hover:text-[#1F2D4B]">
                                    <X size={14} />
                                </button>
                            </div>
                            <input
                                type="url"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applyLink();
                                    }
                                }}
                                placeholder="https://exploremos.mx/promo"
                                autoFocus
                                data-testid={testId ? `${testId}-link-input` : undefined}
                                className="w-full px-2.5 py-2 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                            />
                            <div className="flex gap-1.5 mt-2">
                                <button
                                    type="button"
                                    onClick={applyLink}
                                    data-testid={testId ? `${testId}-link-apply` : undefined}
                                    className="flex-1 h-8 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white text-xs font-medium rounded-md"
                                >
                                    Aplicar
                                </button>
                                {editor.isActive('link') && (
                                    <button
                                        type="button"
                                        onClick={removeLink}
                                        data-testid={testId ? `${testId}-link-remove` : undefined}
                                        className="h-8 px-3 border border-[#ECE3D2] text-[#FB7185] text-xs font-medium rounded-md hover:bg-[#F8F4EC] flex items-center gap-1"
                                    >
                                        <LinkBreak size={12} weight="bold" /> Quitar
                                    </button>
                                )}
                            </div>
                            <p className="text-[10px] text-[#8B8474] mt-1.5">
                                Si omites <code>https://</code> lo agregamos automáticamente.
                            </p>
                        </div>
                    )}
                </div>

                <span className="w-px h-6 bg-[#ECE3D2] mx-0.5" />

                {btn(false,
                    clearAll,
                    <Eraser size={15} weight="bold" />,
                    'Quitar formato',
                    testId ? `${testId}-clear` : undefined)}

                <span className="text-[10px] text-[#8B8474] ml-auto hidden lg:inline">
                    Selecciona texto y aplica formato
                </span>
            </div>

            <div
                className="rounded-md border border-[#ECE3D2] bg-white px-3 py-2.5 focus-within:ring-2 focus-within:ring-[#1F2D4B]/20 focus-within:border-[#1F2D4B]"
                onClick={() => editor.commands.focus()}
            >
                <EditorContent editor={editor} />
            </div>

            {helperText && <p className="text-[11px] text-[#8B8474] mt-1">{helperText}</p>}
        </div>
    );
};

export default RichTextEditor;
